import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import spotService from '../services/spotService';
import futuresService from '../services/futuresService';
import walletService from '../services/walletService';

import apiService from '../services/api';

const TradingContext = createContext();

export const useTradingContext = () => {
  const context = useContext(TradingContext);
  if (!context) {
    throw new Error('useTradingContext must be used within TradingProvider');
  }
  return context;
};

export const TradingProvider = ({ children }) => {
  const [tradingMode, setTradingMode] = useState('spot');
  const [selectedPair, setSelectedPair] = useState(null);
  const [spotPairs, setSpotPairs] = useState([]);
  const [futuresPairs, setFuturesPairs] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
  const [marketPrices, setMarketPrices] = useState({}); // New state for all prices
  const [currentMarketData, setCurrentMarketData] = useState({
    price: 0,
    change: 0,
    changePercent: 0,
    high: 0,
    low: 0,
    volume: 0,
    open: 0
  });
  const selectedPairRef = useRef(selectedPair);

  useEffect(() => {
    selectedPairRef.current = selectedPair;
  }, [selectedPair]);

  // 1. Fonctions de chargement (Callbacks)
  const updateMarketData = useCallback(async (symbol, isBackground = false) => {
    try {
      const data = await apiService.get(`/crypto/ticker/${symbol}`);
      if (data) {
        const newPrice = parseFloat(data.lastPrice) || 0;
        if (isNaN(newPrice) || newPrice <= 0) return;

        setCurrentMarketData(prev => ({
          price: newPrice,
          change: parseFloat(data.priceChange) || 0,
          changePercent: parseFloat(data.priceChangePercent) || 0,
          high: parseFloat(data.highPrice) || 0,
          low: parseFloat(data.lowPrice) || 0,
          volume: parseFloat(data.volume) || 0,
          open: parseFloat(data.openPrice) || newPrice
        }));

        setMarketPrices(prev => ({
            ...prev,
            [symbol]: {
                price: newPrice,
                changePercent: parseFloat(data.priceChangePercent) || 0
            }
        }));
      }
    } catch (error) {
      console.error('Error updating market data:', error);
      const basePrice = symbol.includes('BTC') ? 62345.12 : 
                       symbol.includes('ETH') ? 3623.84 : 100;
      
      setCurrentMarketData(prev => ({
        price: basePrice,
        change: symbol.includes('BTC') ? 2.34 : 0,
        changePercent: symbol.includes('BTC') ? 0.34 : 0,
        high: basePrice * 1.02,
        low: basePrice * 0.98,
        volume: 221370.08,
        open: basePrice * 0.99
      }));
    }
  }, []); // Remove direct price dependency to avoid cycles

  const updateOrderBook = useCallback(async (symbol) => {
    try {
      const data = await apiService.get(`/crypto/orderbook/${symbol}`);
      if (data) {
        setOrderBook({
          bids: data.bids || [],
          asks: data.asks || []
        });
      }
    } catch (error) {
      console.error('Error fetching orderbook:', error);
    }
  }, []);

  const loadAllTickers = useCallback(async () => {
    try {
      const data = await apiService.get('/crypto/ticker');
      if (data && Array.isArray(data)) {
        const newPrices = {};
        data.forEach(t => {
            newPrices[t.symbol] = {
                price: parseFloat(t.lastPrice),
                changePercent: parseFloat(t.priceChangePercent)
            };
        });
        setMarketPrices(prev => ({ ...prev, ...newPrices }));
      }
    } catch (error) {
      console.error('Error loading all tickers:', error);
    }
  }, []);

  const loadTradingPairs = useCallback(async () => {
    try {
      setLoading(true);
      const [spotData, futuresData] = await Promise.all([
        spotService.getTradingPairs(),
        futuresService.getFuturesPairs()
      ]);
      setSpotPairs(Array.isArray(spotData) ? spotData : (spotData?.data || []));
      setFuturesPairs(Array.isArray(futuresData) ? futuresData : (futuresData?.data || []));
    } catch (error) {
      console.error('Error loading trading pairs:', error);
      const fallbackSpot = [
        { id: 1, symbol: 'BTCUSDT', base_symbol: 'BTC', quote_symbol: 'USDT', status: 'TRADING' },
        { id: 2, symbol: 'ETHUSDT', base_symbol: 'ETH', quote_symbol: 'USDT', status: 'TRADING' }
      ];
      setSpotPairs(fallbackSpot);
      setFuturesPairs(fallbackSpot);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWallets = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      setLoading(true);
      const data = await walletService.getWallets();
      const walletType = tradingMode === 'spot' ? 'spot' : 'futures';
      const filteredWallets = Array.isArray(data) 
        ? data.filter(wallet => wallet.wallet_type === walletType)
        : (data.data || []).filter(wallet => wallet.wallet_type === walletType);
      setWallets(filteredWallets);
    } catch (error) {
      console.error('Error loading wallets:', error);
    } finally {
      setLoading(false);
    }
  }, [tradingMode]);

  const getWalletBalance = useCallback((assetStr) => {
    if (!assetStr || !wallets) return 0;
    const w = wallets.find(w => 
      (w.symbol && w.symbol.toUpperCase() === assetStr.toUpperCase()) || 
      (w.asset && w.asset.toString().toUpperCase() === assetStr.toUpperCase())
    );
    if (!w) return 0;
    const balance = parseFloat(w.balance);
    const locked = parseFloat(w.locked || 0);
    return (isNaN(balance) ? 0 : balance) - (isNaN(locked) ? 0 : locked);
  }, [wallets]);

  const loadOrders = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      if (tradingMode === 'spot') {
        const data = await spotService.getUserOrders();
        setOrders(Array.isArray(data) ? data : (data?.data || []));
      } else {
        const data = await futuresService.getFuturesOrders();
        setOrders(Array.isArray(data) ? data : (data?.data || []));
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    }
  }, [tradingMode]);

  const loadPositions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      if (tradingMode === 'futures') {
        const data = await futuresService.getOpenPositions();
        setPositions(Array.isArray(data) ? data : (data?.data || []));
      }
    } catch (error) {
      console.error('Error loading positions:', error);
      setPositions([]);
    }
  }, [tradingMode]);

  // 2. Effets (Hooks)
  useEffect(() => {
    const initializeDefaultPair = () => {
      const defaultPair = {
        symbol: 'BTCUSDT',
        base_symbol: 'BTC',
        quote_symbol: 'USDT',
        id: 1
      };
      setSelectedPair(defaultPair);
      updateMarketData('BTCUSDT');
    };
    initializeDefaultPair();
  }, [updateMarketData]);

  useEffect(() => {
    const init = async () => {
        await loadTradingPairs();
        loadAllTickers();
    };
    init();
  }, [loadTradingPairs, loadAllTickers]);

  useEffect(() => {
    loadWallets();
  }, [loadWallets]);

  useEffect(() => {
    if (selectedPair?.symbol) {
      updateMarketData(selectedPair.symbol);
      updateOrderBook(selectedPair.symbol);
      loadOrders();
      if (tradingMode === 'futures') loadPositions();
      
      const interval = setInterval(() => {
        loadOrders();
        if (tradingMode === 'futures') loadPositions();
        if (Date.now() % 10000 < 5000) {
            updateMarketData(selectedPair.symbol, true);
        }
      }, 5000); 
      
      return () => clearInterval(interval);
    }
  }, [selectedPair, tradingMode, updateMarketData, updateOrderBook, loadOrders, loadPositions]);

  useEffect(() => {
    const SOCKET_URL = process.env.REACT_APP_WS_URL || apiService.getWsUrl();
    let ws;

    const connect = () => {
      const token = localStorage.getItem('token');
      const wsUrl = token ? `${SOCKET_URL}?token=${token}` : SOCKET_URL;
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'prices' && Array.isArray(message.data)) {
            const newPrices = {};
            message.data.forEach(t => {
               newPrices[t.s] = {
                   price: parseFloat(t.c),
                   changePercent: parseFloat(t.P)
               };
            });
            setMarketPrices(prev => ({ ...prev, ...newPrices }));

            const activePair = selectedPairRef.current;
            if (activePair?.symbol) {
                const ticker = message.data.find(t => t.s === activePair.symbol);
                if (ticker) {
                  setCurrentMarketData(prev => ({
                    ...prev,
                    price: parseFloat(ticker.c),
                    change: parseFloat(ticker.p),
                    changePercent: parseFloat(ticker.P),
                    high: parseFloat(ticker.h),
                    low: parseFloat(ticker.l),
                    volume: parseFloat(ticker.v)
                  }));
                }
            }
          } else if (message.type === 'refresh_trading') {
            // Server pushes this when a TP/SL, limit order or position changes
            loadOrders();
            loadPositions();
            loadWallets();
          }
        } catch (err) {
          console.error('Error parsing WS message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Erreur WebSocket:', error);
      };

      ws.onclose = (event) => {
        setTimeout(() => {
          if (mounted) connect();
        }, 5000);
      };
    };

    let mounted = true;
    connect();

    return () => {
      mounted = false;
      if (ws) {
        // Supprimer les handlers pour éviter les erreurs de state update sur un composant démonté
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        } else if (ws.readyState === WebSocket.CONNECTING) {
          ws.addEventListener('open', () => ws.close(), { once: true });
        }
      }
    };
  }, []);

  // 3. Actions utilisateur
  const createOrder = async (orderData) => {
    try {
      setLoading(true);
      let result;
      if (tradingMode === 'spot') {
        result = await spotService.createOrder(orderData);
        await Promise.allSettled([loadOrders(), loadWallets()]);
      } else {
        result = await futuresService.createOrder(orderData);
        await Promise.allSettled([loadOrders(), loadPositions(), loadWallets()]);
      }
      return result;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      setLoading(true);
      if (tradingMode === 'futures') {
        await futuresService.cancelOrder(orderId);
        await Promise.allSettled([loadOrders(), loadPositions(), loadWallets()]);
      } else {
        await spotService.cancelOrder(orderId);
        await Promise.allSettled([loadOrders(), loadWallets()]);
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const closePosition = async (positionId) => {
    try {
      setLoading(true);
      await futuresService.closePosition(positionId);
      await loadPositions();
      await loadWallets();
    } catch (error) {
      console.error('Error closing position:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updatePositionTPSL = async (positionId, take_profit, stop_loss) => {
    try {
      setLoading(true);
      await futuresService.updateTPSL(positionId, { take_profit, stop_loss });
      await Promise.allSettled([loadPositions()]);
    } catch (error) {
      console.error('Error updating TP/SL:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await loadTradingPairs();
    await loadWallets();
    await loadOrders();
    if (tradingMode === 'futures') await loadPositions();
    if (selectedPair?.symbol) await updateMarketData(selectedPair.symbol);
  };

  const value = {
    tradingMode,
    setTradingMode,
    selectedPair,
    setSelectedPair,
    spotPairs,
    futuresPairs,
    wallets,
    orders,
    positions,
    loading,
    marketPrices,
    currentMarketData,
    orderBook,
    updateMarketData,
    getWalletBalance,
    loadOrders,
    loadPositions,
    createOrder,
    cancelOrder,
    closePosition,
    updatePositionTPSL,
    refreshData
  };

  return (
    <TradingContext.Provider value={value}>
      {children}
    </TradingContext.Provider>
  );
};